import { ehModoJogo, type Camera, type ModoJogo } from '@core/tipos';
import type { EstadoJogo, Instantaneo } from '@core/motor/estados';
import { instantaneoDe } from '@core/motor/estados';
import { MULTIPLICADOR_MINIMO } from '@core/modos/trama';

/**
 * Formato da sessão salva (§12).
 *
 * O estado do jogo já é serializável por construção (ver `EstadoJogo`), então
 * a serialização é quase identidade. O que este módulo adiciona é a **fronteira
 * de versão**: nada entra no jogo sem passar por `migrar`.
 */

/**
 * Versão 2: o estado ganhou modo de jogo e pontuação (§22). Sessões da versão
 * 1 continuam válidas — são partidas Bistrô sem placar, e é isso que `de1Para2`
 * escreve nelas.
 */
export const VERSAO_SCHEMA = 2;
export const NOME_BANCO = 'entreletras';
export const NOME_ARMAZEM = 'sessao';
export const CHAVE_SESSAO = 'atual';
/**
 * A partida da Trama mora em chave própria (ADR-0009).
 *
 * A partida diária tem ciclo de vida diferente da sessão em cadeia, que é
 * contínua: apagar a cadeia — trocar de modo (§22.5), recomeçar no Bistrô —
 * levaria o dia junto, e o dia de hoje já foi jogado. Separadas, o tabuleiro
 * resolvido continua lá quando a pessoa volta, mesmo que tenha ido jogar outro
 * modo no meio (§23.1).
 */
export const CHAVE_TERMO = 'termo';
/**
 * O arcade mora em chave própria, pela mesma razão da Trama (ADR-0009).
 *
 * §22.7 promete que o nível volta em outra sessão. Dividir a chave com a cadeia
 * comum quebraria essa promessa no primeiro `limpar`: trocar de modo apagaria
 * o progresso de dezenas de níveis junto com um tabuleiro de Bistrô.
 */
export const CHAVE_PALAVREDO = 'palavredo';
/** Modo em curso, para a página abrir onde a pessoa parou (§22.5). */
export const CHAVE_MODO = 'modo';
/** Som ligado ou desligado. Sobrevive a `limpar`, que é só da cadeia (§22.5). */
export const CHAVE_SOM = 'som';
export const CHAVE_DIAGNOSTICO = 'sessao-incompativel';

/**
 * Onde a partida de cada modo é guardada.
 *
 * Uma função, e não um `if` espalhado: quem grava e quem lê têm de concordar,
 * e foi por discordarem que a partida do dia se perdia ao recarregar.
 */
export function chaveDaPartida(modo: ModoJogo): string {
  if (modo === 'termo') return CHAVE_TERMO;
  if (modo === 'palavredo') return CHAVE_PALAVREDO;
  return CHAVE_SESSAO;
}

export interface SessaoSalva {
  readonly versaoSchema: number;
  /** Epoch ms. Injetado por quem chama — o núcleo não lê o relógio. */
  readonly salvoEm: number;
  readonly jogo: Instantaneo;
  readonly camera: Camera;
}

export function serializar(estado: EstadoJogo, camera: Camera, agora: number): SessaoSalva {
  const jogo = instantaneoDe(estado);
  return {
    versaoSchema: VERSAO_SCHEMA,
    salvoEm: agora,
    // O anúncio é efêmero: não faz sentido reanunciar ao restaurar.
    jogo: { ...jogo, anuncio: undefined },
    camera,
  };
}

export type ResultadoRestauracao =
  | { readonly ok: true; readonly sessao: SessaoSalva }
  | { readonly ok: false; readonly motivo: 'ausente' }
  | { readonly ok: false; readonly motivo: 'incompativel'; readonly bruto: unknown };

/**
 * Valida a forma do que veio do banco. É deliberadamente conservador: qualquer
 * dúvida vira `incompativel`, e §17 manda preservar o bruto para diagnóstico e
 * começar uma sessão nova.
 *
 * Só aceita a versão corrente: quem vem de uma versão antiga passa antes por
 * `migrar`, que é o único lugar onde um salto de schema acontece.
 */
export function desserializar(bruto: unknown): ResultadoRestauracao {
  if (bruto === undefined || bruto === null) return { ok: false, motivo: 'ausente' };

  if (typeof bruto !== 'object') return { ok: false, motivo: 'incompativel', bruto };
  const sessao = bruto as Partial<SessaoSalva>;

  if (sessao.versaoSchema !== VERSAO_SCHEMA) return { ok: false, motivo: 'incompativel', bruto };
  if (typeof sessao.jogo !== 'object' || sessao.jogo === null) {
    return { ok: false, motivo: 'incompativel', bruto };
  }
  if (typeof sessao.camera !== 'object' || sessao.camera === null) {
    return { ok: false, motivo: 'incompativel', bruto };
  }

  const jogo = sessao.jogo as Partial<Instantaneo>;
  // O schema não subiu de versão com o Nitro nem com o Palavredo:
  // `duracaoSegundos`, `terminaEm`, `nivel` e `nivelDesde` são opcionais, então
  // toda sessão v2 continua válida como está.
  //
  // A lista de modos aceitos já foi escrita à mão aqui, e faltou o `termo`: a
  // Trama gravava a partida como todo modo grava, e a leitura a recusava por
  // modo desconhecido — quem recarregava perdia o tabuleiro do dia e ainda via
  // o aviso de sessão incompatível (§23.1). O mesmo esquecimento se repetiu com
  // o Palavredo, noutro arquivo. `ehModoJogo` é a lista única que o compilador
  // confere, e é por isso que ela não mora mais aqui.
  if (!ehModoJogo(jogo.modo)) {
    return { ok: false, motivo: 'incompativel', bruto };
  }
  if (
    typeof jogo.semente !== 'number' ||
    typeof jogo.pontos !== 'number' ||
    typeof jogo.draft !== 'string' ||
    typeof jogo.cadeia !== 'object' ||
    jogo.cadeia === null ||
    !Array.isArray((jogo.cadeia as { palavras?: unknown }).palavras)
  ) {
    return { ok: false, motivo: 'incompativel', bruto };
  }

  return { ok: true, sessao: sessao as SessaoSalva };
}

/**
 * v1 → v2: modo e pontuação não existiam (§22).
 *
 * Toda sessão da v1 é uma partida Bistrô, que é o modo sem placar e sem fim — a
 * conversão não inventa pontos para uma cadeia que foi jogada sem eles.
 */
function de1Para2(sessao: Record<string, unknown>): Record<string, unknown> {
  const jogo = sessao.jogo as Record<string, unknown>;
  return {
    ...sessao,
    versaoSchema: 2,
    jogo: { ...jogo, modo: 'zen', pontos: 0, multiplicador: MULTIPLICADOR_MINIMO },
  };
}

/** Cada salto de versão, em ordem. `migrar` os aplica até a versão corrente. */
const SALTOS: Readonly<Record<number, (s: Record<string, unknown>) => Record<string, unknown>>> = {
  1: de1Para2,
};

/**
 * Ponto único de migração: nada entra no jogo sem passar por aqui.
 *
 * Aplica um salto de cada vez até chegar à versão corrente. Uma versão sem
 * salto registrado — futura, ou corrompida — cai em `desserializar`, que a
 * recusa e deixa §17 preservar o bruto.
 */
export function migrar(bruto: unknown): ResultadoRestauracao {
  if (bruto === undefined || bruto === null) return { ok: false, motivo: 'ausente' };
  if (typeof bruto !== 'object') return { ok: false, motivo: 'incompativel', bruto };

  let atual = bruto as Record<string, unknown>;
  while (typeof atual.versaoSchema === 'number' && atual.versaoSchema < VERSAO_SCHEMA) {
    const salto = SALTOS[atual.versaoSchema];
    if (salto === undefined) break;
    if (typeof atual.jogo !== 'object' || atual.jogo === null) break;
    atual = salto(atual);
  }

  return desserializar(atual);
}
